import { Mail, Plug, RefreshCw, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/EmptyState'
import { Toolbar } from '@/components/Toolbar'
import { formatDate } from '@/lib/locale'
import type { InboxAccount } from '@/features/inbox/types'
import { SyncIntervalControl } from './SyncIntervalControl'
import { BackfillProgress } from './BackfillProgress'

function statusBadge(status: InboxAccount['status']) {
  if (status === 'connected') return <Badge variant="success">Connected</Badge>
  if (status === 'error') return <Badge variant="danger">Error</Badge>
  return <Badge variant="secondary">Disconnected</Badge>
}

interface AccountsPanelProps {
  accounts: InboxAccount[]
  loading: boolean
  canConnect: boolean
  canSync: boolean
  canDisconnect: boolean
  canManageAutoSync: boolean
  connectPending: boolean
  syncPending: boolean
  onConnect: () => void
  onSync: (account: InboxAccount) => void
  onRequestDisconnect: (account: InboxAccount) => void
}

export function AccountsPanel({
  accounts,
  loading,
  canConnect,
  canSync,
  canDisconnect,
  canManageAutoSync,
  connectPending,
  syncPending,
  onConnect,
  onSync,
  onRequestDisconnect,
}: AccountsPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Connected accounts</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : accounts.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="No inbox connected yet"
            description="Connect a Gmail account to start pulling and categorizing your mail."
            action={
              canConnect ? (
                <Button onClick={onConnect} disabled={connectPending}>
                  <Plug className="size-4" />
                  Connect Gmail
                </Button>
              ) : null
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {accounts.map((a) => (
              <li key={a.id} className="flex flex-col gap-2 py-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Mail className="size-4 text-muted-foreground" />
                      <span className="truncate font-medium">{a.email}</span>
                      {statusBadge(a.status)}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {a.last_sync_at ? `Last sync ${formatDate(a.last_sync_at)}` : 'Never synced'}
                      {a.last_error ? ` · ${a.last_error}` : ''}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    {canManageAutoSync ? <SyncIntervalControl account={a} /> : null}
                    <Toolbar align="end">
                      {canSync ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onSync(a)}
                          disabled={syncPending}
                        >
                          <RefreshCw className="size-4" />
                          Sync now
                        </Button>
                      ) : null}
                      {canDisconnect ? (
                        <Button variant="ghost" size="sm" onClick={() => onRequestDisconnect(a)}>
                          <Trash2 className="size-4 text-destructive" />
                          Disconnect
                        </Button>
                      ) : null}
                    </Toolbar>
                  </div>
                </div>
                <BackfillProgress account={a} canImportOlder={canSync} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
